import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function formatAge(v) {
  if (!v) return "0";
  return v === Math.floor(v) ? String(Math.floor(v)) : String(v);
}

export default function PublicCoursesPage() {
  const { courseId } = useParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [view, setView] = useState("grid"); // "grid" | "detail" | "form"

  const refreshCourses = () => {
    axios.get(`${API_BASE}/api/courses/public/active`)
      .then(res => setCourses(res.data)).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API_BASE}/api/courses/public/active`)
      .then(res => {
        setCourses(res.data);
        // Deep-Link: Wenn courseId in URL, direkt Detailseite anzeigen
        if (courseId) {
          const match = res.data.find(c => c.id === courseId);
          if (match) {
            setSelectedCourse(match);
            setView("detail");
          }
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [courseId]);

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return d; }
  };

  const formatDateLong = (d) => {
    if (!d) return "";
    try { return new Date(d + "T00:00:00").toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return d; }
  };

  const formatDeadline = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d + "T20:00:00");
      return dt.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) + ", 20:00 Uhr";
    } catch { return d; }
  };

  const goToDetail = (course) => { setSelectedCourse(course); setView("detail"); };
  const goToForm = () => { setView("form"); };
  const goToGrid = () => { setSelectedCourse(null); setView("grid"); };
  const goBackToDetail = () => { setView("detail"); };

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
      maxWidth: 860,
      margin: "0 auto",
      padding: "24px 16px",
    }}>
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>Kurse werden geladen...</div>
      ) : courses.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
          Aktuell sind keine Schwimmkurse zur Anmeldung verfügbar.
        </div>
      ) : view === "form" && selectedCourse ? (
        <RegistrationForm
          course={selectedCourse}
          formatDate={formatDate}
          onBack={goBackToDetail}
          onSuccess={() => { refreshCourses(); goToGrid(); }}
        />
      ) : view === "detail" && selectedCourse ? (
        <CourseDetailView
          course={selectedCourse}
          formatDate={formatDate}
          formatDateLong={formatDateLong}
          formatDeadline={formatDeadline}
          onBack={goToGrid}
          onRegister={goToForm}
        />
      ) : (
        <div data-grid="courses" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "24px" }}>
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              formatDate={formatDate}
              formatDeadline={formatDeadline}
              onClick={() => goToDetail(course)}
            />
          ))}
        </div>
      )}
      <style>{`
        @media (max-width: 640px) {
          div[data-grid="courses"] { grid-template-columns: 1fr !important; }
          div[data-layout="detail-header"] { flex-direction: column !important; }
          div[data-layout="detail-header"] > div:last-child { order: -1; margin-bottom: 16px; width: 100% !important; }
          div[data-layout="detail-header"] > div:last-child img { width: 100% !important; max-height: 260px !important; }
        }
        div[data-testid="detail-description"] h1,
        div[data-testid="detail-description"] h2,
        div[data-testid="detail-description"] h3,
        div[data-testid="detail-description"] h4 {
          margin: 20px 0 8px; font-weight: 700;
        }
        div[data-testid="detail-description"] h3 { font-size: 17px; }
        div[data-testid="detail-description"] h4 { font-size: 15px; }
        div[data-testid="detail-description"] p {
          margin: 0 0 12px; line-height: 1.7;
        }
        div[data-testid="detail-description"] ul,
        div[data-testid="detail-description"] ol {
          margin: 8px 0 16px; padding-left: 24px;
        }
        div[data-testid="detail-description"] ul { list-style-type: disc; }
        div[data-testid="detail-description"] ol { list-style-type: decimal; }
        div[data-testid="detail-description"] li {
          margin-bottom: 6px; line-height: 1.6; padding-left: 4px;
        }
        div[data-testid="detail-description"] a {
          color: #2563eb; text-decoration: underline;
        }
        div[data-testid="detail-description"] strong {
          font-weight: 700;
        }
      `}</style>
    </div>
  );
}


/* ===== Spots Badge ===== */
function SpotsBadge({ course, style }) {
  const baseStyle = { padding: "5px 14px", borderRadius: 4, fontSize: 13, fontWeight: 600, color: "#fff", display: "inline-block", ...style };
  if (course.is_full) {
    return <span data-testid="spots-badge-red" style={{ ...baseStyle, background: "#ef4444" }}>ausgebucht</span>;
  }
  const free = course.spots_available;
  const bg = free <= 5 ? "#eab308" : "#22c55e";
  const testId = free <= 5 ? "spots-badge-orange" : "spots-badge-green";
  return <span data-testid={testId} style={{ ...baseStyle, background: bg }}>{free} freie Plätze</span>;
}


/* ===== Course Card (Grid) ===== */
function CourseCard({ course, formatDate, formatDeadline, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#fff", cursor: "pointer", transition: "box-shadow 0.2s" }}
      className="hover:shadow-lg"
      data-testid={`public-course-${course.id}`}
    >
      <div style={{ position: "relative", width: "100%", paddingTop: "60%", overflow: "hidden", background: "#e2e8f0" }}>
        {course.image_url && (
          <img src={`${API_BASE}${course.image_url}`} alt="" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        <SpotsBadge course={course} style={{ position: "absolute", bottom: 10, right: 10 }} />
      </div>
      <div style={{ padding: "14px 16px 16px" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#1a202c", lineHeight: 1.3 }}>{course.title}</h3>
        <p style={{ fontSize: 13, color: "#4a5568", margin: "3px 0" }}>ab {formatDate(course.start_date)}</p>
        {course.weekday_time && <p style={{ fontSize: 13, color: "#4a5568", margin: "3px 0" }}>{course.weekday_time}</p>}
        {(course.min_age_years > 0 || course.max_age_years > 0) && (
          <p style={{ fontSize: 13, color: "#4a5568", margin: "3px 0", display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="14" height="14" fill="none" stroke="#64748b" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="7" r="4" strokeWidth="1.5"/>
            </svg>
            <span>Alter: {formatAge(course.min_age_years)}–{course.max_age_years > 0 ? formatAge(course.max_age_years) : "∞"} Jahre</span>
          </p>
        )}
        {course.location && (
          <p style={{ fontSize: 13, color: "#4a5568", margin: "3px 0", display: "flex", alignItems: "flex-start", gap: 4 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#3b82f6" style={{ flexShrink: 0, marginTop: 2 }}>
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>{course.location}</span>
          </p>
        )}
        {course.registration_deadline && (
          <p style={{ fontSize: 12, color: "#64748b", marginTop: 10, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
            Anmeldeschluss: {formatDeadline(course.registration_deadline)}
          </p>
        )}
      </div>
    </div>
  );
}


/* ===== Course Detail View (Yolawo-style) ===== */
function CourseDetailView({ course, formatDate, formatDateLong, formatDeadline, onBack, onRegister }) {
  const [descOpen, setDescOpen] = useState(true);

  const iconStyle = { flexShrink: 0, marginTop: 2, color: "#64748b" };

  return (
    <div data-testid="course-detail-view">
      {/* Zurück */}
      <button onClick={onBack} data-testid="detail-back-btn" style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 14px", fontSize: 14, cursor: "pointer", color: "#374151", display: "flex", alignItems: "center", gap: 6, marginBottom: 24 }}>
        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Zurück
      </button>

      {/* Titel */}
      <h1 style={{ fontSize: 26, fontWeight: 700, color: "#1a202c", margin: "0 0 20px", lineHeight: 1.3 }} data-testid="detail-title">
        {course.title}
      </h1>

      {/* Header: Info links, Bild rechts */}
      <div data-layout="detail-header" style={{ display: "flex", gap: 24, marginBottom: 24 }}>
        {/* Info-Karte */}
        <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          {/* Datum & Uhrzeit */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
            <svg style={iconStyle} width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="1.5"/><path d="M16 2v4M8 2v4M3 10h18" strokeWidth="1.5"/></svg>
            <div>
              <p style={{ margin: 0, fontSize: 14, color: "#1a202c" }}>ab {formatDate(course.start_date)}</p>
              {course.weekday_time && <p style={{ margin: "2px 0 0", fontSize: 14, color: "#4a5568" }}>{course.weekday_time}</p>}
            </div>
          </div>

          {/* Ort */}
          {course.location && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
              <svg style={iconStyle} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" strokeWidth="1.5"/><circle cx="12" cy="9" r="2.5" strokeWidth="1.5"/></svg>
              <p style={{ margin: 0, fontSize: 14, color: "#1a202c" }}>{course.location}</p>
            </div>
          )}

          {/* Altersgruppe */}
          {(course.min_age_years > 0 || course.max_age_years > 0) && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
              <svg style={iconStyle} width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="7" r="4" strokeWidth="1.5"/>
              </svg>
              <p style={{ margin: 0, fontSize: 14, color: "#1a202c" }}>
                Alter: {formatAge(course.min_age_years)}–{course.max_age_years > 0 ? formatAge(course.max_age_years) : "∞"} Jahre
              </p>
            </div>
          )}

          {/* Anmeldeschluss */}
          {course.registration_deadline && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
              <svg style={iconStyle} width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="1.5"/><path d="M12 6v6l4 2" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <p style={{ margin: 0, fontSize: 14, color: "#1a202c" }}>Anmeldeschluss: {formatDeadline(course.registration_deadline)}</p>
            </div>
          )}

          {/* Angebotsbeschreibung Toggle */}
          <button
            onClick={() => setDescOpen(!descOpen)}
            data-testid="detail-desc-toggle"
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", width: "100%", background: "none", border: "none", cursor: "pointer", justifyContent: "space-between" }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg style={{ ...iconStyle, marginTop: 0 }} width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="1.5"/><path d="M12 16v-4M12 8h.01" strokeWidth="2" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 14, fontWeight: 500, color: "#1a202c" }}>Angebotsbeschreibung</span>
            </span>
            <svg width="18" height="18" fill="none" stroke="#64748b" viewBox="0 0 24 24" style={{ transform: descOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Teaserbild */}
        {course.image_url && (
          <div style={{ flexShrink: 0, width: 280 }}>
            <img
              src={`${API_BASE}${course.image_url}`}
              alt=""
              style={{ width: "100%", borderRadius: 8, objectFit: "cover", maxHeight: 220 }}
              data-testid="detail-teaser-image"
            />
          </div>
        )}
      </div>

      {/* Beschreibung (aufgeklappt) */}
      {descOpen && course.description_html && (
        <div
          data-testid="detail-description"
          style={{ fontSize: 14, lineHeight: 1.7, color: "#374151", marginBottom: 24, paddingBottom: 24, borderBottom: "1px solid #e2e8f0" }}
          dangerouslySetInnerHTML={{ __html: course.description_html }}
        />
      )}

      {/* Beginn der Schwimmausbildung */}
      {course.start_date && (
        <p style={{ fontSize: 15, fontWeight: 600, color: "#1a202c", marginBottom: 28 }} data-testid="detail-start-date">
          Beginn der Schwimmausbildung: {formatDateLong(course.start_date)}
        </p>
      )}

      {/* Zur Anmeldung Button */}
      <div style={{ position: "relative", maxWidth: 500, margin: "0 auto" }}>
        <div style={{ position: "absolute", top: -14, right: 0, zIndex: 1 }}>
          <SpotsBadge course={course} />
        </div>
        <button
          onClick={course.is_full ? undefined : onRegister}
          disabled={course.is_full}
          data-testid="goto-registration-btn"
          style={{
            width: "100%",
            padding: "16px 24px",
            background: course.is_full ? "#94a3b8" : "#003366",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 18,
            cursor: course.is_full ? "not-allowed" : "pointer",
            opacity: course.is_full ? 0.7 : 1,
            transition: "background 0.2s",
          }}
        >
          {course.is_full ? "Ausgebucht" : "Zur Anmeldung"}
        </button>
      </div>
    </div>
  );
}


/* ===== Registration Form (mit Quereinstieg) ===== */
function RegistrationForm({ course, formatDate, onBack, onSuccess }) {
  const [form, setForm] = useState({
    child_first_name: "",
    child_last_name: "",
    child_birth_date: "",
    gender: "",
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    email2: "",
    phone: "",
    phone2: "",
    phone3: "",
    notes: "",
    is_beginner_confirmed: false,
    agb_accepted: false,
    experience_description: "",
  });

  const formatPhone = (raw) => {
    let d = raw.replace(/[^\d+]/g, "");
    if (d.startsWith("0")) d = "+49" + d.substring(1);
    else if (d.startsWith("49")) d = "+" + d;
    else if (d && !d.startsWith("+")) d = "+49" + d;
    const digits = d.replace(/[^\d]/g, "");
    if (digits.length <= 2) return d;
    let r = digits.substring(2);
    const p = [r.substring(0, 3)];
    r = r.substring(3);
    while (r.length > 4) { p.push(r.substring(0, 4)); r = r.substring(4); }
    if (r) p.push(r);
    return "+49 " + p.join(" ");
  };
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [ageWarning, setAgeWarning] = useState(null);
  const [mode, setMode] = useState("register"); // "register" | "lateral"

  const inputStyle = {
    width: "100%", borderRadius: 8, border: "1px solid #d1d5db", padding: "10px 12px",
    fontSize: 14, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 4 };

  const validateAge = (birthStr) => {
    setAgeWarning(null);
    if (!birthStr || !course.start_date) return;
    let bd;
    const parts = birthStr.split(".");
    if (parts.length === 3 && parts[2].length === 4) {
      bd = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
      bd = new Date(birthStr);
    }
    if (isNaN(bd.getTime())) return;
    const sd = new Date(course.start_date + "T00:00:00");
    const ageDiff = (sd - bd) / (365.25 * 24 * 60 * 60 * 1000);
    const minAge = course.min_age_years || 0;
    const maxAge = course.max_age_years || 0;
    if (ageDiff < minAge) {
      setAgeWarning(`Ihr Kind ist zum Kursbeginn (${formatDate(course.start_date)}) erst ${Math.floor(ageDiff * 10) / 10} Jahre alt. Das Mindestalter beträgt ${formatAge(minAge)} Jahre. Bitte wählen Sie einen passenden Kurs.`);
    } else if (maxAge > 0 && ageDiff >= maxAge) {
      setAgeWarning(`Ihr Kind ist zum Kursbeginn (${formatDate(course.start_date)}) bereits ${Math.floor(ageDiff * 10) / 10} Jahre alt. Das Höchstalter für diesen Kurs beträgt ${formatAge(maxAge)} Jahre. Bitte wählen Sie einen passenden Kurs für die Altersgruppe Ihres Kindes.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (mode === "lateral") {
      if (!form.experience_description.trim()) { setError("Bitte beschreiben Sie die Vorkenntnisse Ihres Kindes."); return; }
      if (!form.gender) { setError("Bitte wählen Sie das Geschlecht des Kindes aus."); return; }
      if (!form.agb_accepted) { setError("Bitte akzeptieren Sie die Datenschutzerklärung."); return; }
      setSubmitting(true);
      try {
        const res = await axios.post(`${API_BASE}/api/courses/public/${course.id}/lateral-entry`, {
          child_first_name: form.child_first_name,
          child_last_name: form.child_last_name,
          child_birth_date: form.child_birth_date,
          gender: form.gender,
          parent_first_name: form.parent_first_name,
          parent_last_name: form.parent_last_name,
          email: form.email,
          email2: form.email2,
          phone: form.phone,
          phone2: form.phone2,
          phone3: form.phone3,
          notes: form.notes,
          experience_description: form.experience_description,
        });
        setSuccess(res.data.message);
      } catch (err) {
        setError(err.response?.data?.detail || "Ein Fehler ist aufgetreten.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!form.is_beginner_confirmed) { setError("Bitte bestätigen Sie, dass Ihr Kind Schwimmanfänger ist."); return; }
    if (!form.gender) { setError("Bitte wählen Sie das Geschlecht des Kindes aus."); return; }
    if (!form.agb_accepted) { setError("Bitte akzeptieren Sie die Datenschutzerklärung."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/courses/public/${course.id}/register`, {
        child_first_name: form.child_first_name,
        child_last_name: form.child_last_name,
        child_birth_date: form.child_birth_date,
        gender: form.gender,
        parent_first_name: form.parent_first_name,
        parent_last_name: form.parent_last_name,
        email: form.email,
        email2: form.email2,
        phone: form.phone,
        phone2: form.phone2,
        phone3: form.phone3,
        notes: form.notes,
        is_beginner_confirmed: form.is_beginner_confirmed,
      });
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Ein Fehler ist aufgetreten.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center", padding: "32px 16px" }} data-testid="registration-success">
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: mode === "lateral" ? "#dbeafe" : "#dcfce7", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
          {mode === "lateral" ? (
            <svg width="28" height="28" fill="none" stroke="#2563eb" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
          ) : (
            <svg width="28" height="28" fill="none" stroke="#16a34a" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          )}
        </div>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: "#1a202c" }}>
          {mode === "lateral" ? "Anfrage gesendet!" : "Anmeldung erfolgreich!"}
        </h3>
        <p style={{ color: "#4a5568", marginTop: 12 }}>{success}</p>
        {mode !== "lateral" && <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 8 }}>Prüfen Sie auch Ihren Spam-Ordner.</p>}
        <button onClick={onSuccess} style={{ marginTop: 20, padding: "10px 24px", background: "#003366", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
          Zurück zur Übersicht
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }} data-testid="registration-form">
      <button onClick={onBack} data-testid="form-back-btn" style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 14px", fontSize: 14, cursor: "pointer", color: "#374151", display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Zurück
      </button>

      <h3 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 4px" }}>{course.title}</h3>
      <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 24px" }}>{course.weekday_time}</p>

      <form onSubmit={handleSubmit}>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, fontSize: 14, color: "#b91c1c", marginBottom: 16 }} data-testid="registration-error">{error}</div>}

        <p style={{ fontSize: 15, fontWeight: 600, color: "#1a202c", margin: "0 0 12px" }}>Daten des Kindes</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={labelStyle}>Vorname *</label><input data-testid="reg-child-first-name" required style={inputStyle} value={form.child_first_name} onChange={e => setForm({...form, child_first_name: e.target.value})} /></div>
          <div><label style={labelStyle}>Nachname *</label><input data-testid="reg-child-last-name" required style={inputStyle} value={form.child_last_name} onChange={e => setForm({...form, child_last_name: e.target.value})} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Geburtsdatum *</label>
            <input
              data-testid="reg-child-birth-date"
              required
              placeholder="TT.MM.JJJJ"
              style={{ ...inputStyle, borderColor: ageWarning ? "#f59e0b" : "#d1d5db" }}
              value={form.child_birth_date}
              onChange={e => {
                setForm({...form, child_birth_date: e.target.value});
                validateAge(e.target.value);
              }}
            />
            <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
              Alter: {formatAge(course.min_age_years)}{course.max_age_years > 0 ? `–${formatAge(course.max_age_years)}` : "+"} Jahre
            </p>
            {ageWarning && (
              <div data-testid="age-warning" style={{ marginTop: 8, padding: 12, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 13, color: "#92400e", lineHeight: 1.5 }}>
                <strong style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                  <svg width="16" height="16" fill="none" stroke="#d97706" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86l-8.6 14.86A1 1 0 002.56 20h18.88a1 1 0 00.87-1.5l-8.6-14.86a1 1 0 00-1.74 0z"/></svg>
                  Altershinweis
                </strong>
                {ageWarning}
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Geschlecht *</label>
            <select data-testid="reg-gender" required style={inputStyle} value={form.gender} onChange={e => setForm({...form, gender: e.target.value})}>
              <option value="">Bitte wählen</option>
              <option value="Junge">Junge</option>
              <option value="Mädchen">Mädchen</option>
              <option value="Divers">Divers</option>
            </select>
          </div>
        </div>

        <p style={{ fontSize: 15, fontWeight: 600, color: "#1a202c", margin: "0 0 12px" }}>Daten des Buchenden</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={labelStyle}>Vorname *</label><input data-testid="reg-parent-first-name" required style={inputStyle} value={form.parent_first_name} onChange={e => setForm({...form, parent_first_name: e.target.value})} /></div>
          <div><label style={labelStyle}>Nachname *</label><input data-testid="reg-parent-last-name" required style={inputStyle} value={form.parent_last_name} onChange={e => setForm({...form, parent_last_name: e.target.value})} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={labelStyle}>E-Mail *</label><input data-testid="reg-email" type="email" required style={inputStyle} value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div><label style={labelStyle}>E-Mail 2 (2. Elternteil)</label><input data-testid="reg-email2" type="email" style={inputStyle} value={form.email2} onChange={e => setForm({...form, email2: e.target.value})} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={labelStyle}>Telefon *</label><input data-testid="reg-phone" type="tel" required style={inputStyle} placeholder="+49 170 1234 5678" value={form.phone} onChange={e => setForm({...form, phone: formatPhone(e.target.value)})} /></div>
          <div><label style={labelStyle}>Telefon 2</label><input data-testid="reg-phone2" type="tel" style={inputStyle} placeholder="+49..." value={form.phone2} onChange={e => setForm({...form, phone2: formatPhone(e.target.value)})} /></div>
          <div><label style={labelStyle}>Telefon 3</label><input data-testid="reg-phone3" type="tel" style={inputStyle} placeholder="+49..." value={form.phone3} onChange={e => setForm({...form, phone3: formatPhone(e.target.value)})} /></div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Allgemeine Bemerkungen</label>
          <textarea data-testid="reg-notes" style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} placeholder="Allergien, besondere Hinweise, etc." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
        </div>

        <p style={{ fontSize: 15, fontWeight: 600, color: "#1a202c", margin: "0 0 12px" }}>Bestätigungen</p>

        {mode === "register" ? (
          <>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 12, border: `1px solid ${!form.is_beginner_confirmed && error ? '#f87171' : '#e2e8f0'}`, borderRadius: 8, marginBottom: 10, cursor: "pointer" }}>
              <input data-testid="reg-beginner-checkbox" type="checkbox" checked={form.is_beginner_confirmed} onChange={e => setForm({...form, is_beginner_confirmed: e.target.checked})} style={{ marginTop: 2, width: 18, height: 18 }} />
              <span style={{ fontSize: 13, color: "#374151" }}>Mein Kind ist <strong>Schwimmanfänger</strong> und hat noch keinerlei Vorkenntnisse wie z.B. Seepferdchen oder das Bronzeabzeichen. *</span>
            </label>

            {/* Quereinstieg-Hinweis wenn Checkbox nicht gesetzt */}
            {!form.is_beginner_confirmed && (
              <div data-testid="lateral-entry-hint" style={{ margin: "12px 0", padding: 14, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8 }}>
                <p style={{ fontSize: 13, color: "#1e40af", margin: "0 0 8px", lineHeight: 1.5 }}>
                  Ihr Kind hat bereits Schwimmerfahrung? In Einzelfällen sind <strong>Quereinstiege</strong> möglich.
                </p>
                <button
                  type="button"
                  data-testid="lateral-entry-switch-btn"
                  onClick={() => setMode("lateral")}
                  style={{ fontSize: 13, fontWeight: 600, color: "#2563eb", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  Unverbindliche Quereinstieg-Anfrage stellen
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Quereinstieg-Modus */}
            <div style={{ margin: "0 0 12px", padding: 14, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8 }}>
              <p style={{ fontSize: 13, color: "#1e40af", margin: 0, fontWeight: 600 }}>
                Quereinstieg-Anfrage
              </p>
              <p style={{ fontSize: 12, color: "#3b82f6", margin: "4px 0 0" }}>
                Ihre Anfrage wird geprüft. Wir melden uns bei Ihnen, falls ein Platz verfügbar ist.
              </p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Vorkenntnisse Ihres Kindes *</label>
              <textarea
                data-testid="lateral-entry-experience"
                required
                placeholder="Welche Schwimmerfahrung hat Ihr Kind? (z.B. Seepferdchen, Bronzeabzeichen, Schwimmverein, etc.)"
                style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                value={form.experience_description}
                onChange={e => setForm({...form, experience_description: e.target.value})}
              />
            </div>
            <button
              type="button"
              onClick={() => setMode("register")}
              style={{ fontSize: 13, color: "#64748b", background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 12, textDecoration: "underline" }}
            >
              Doch Schwimmanfänger? Zur regulären Anmeldung
            </button>
          </>
        )}

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 12, border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 20, cursor: "pointer" }}>
          <input data-testid="reg-agb-checkbox" type="checkbox" checked={form.agb_accepted} onChange={e => setForm({...form, agb_accepted: e.target.checked})} style={{ marginTop: 2, width: 18, height: 18 }} />
          <span style={{ fontSize: 13, color: "#374151" }}>Ich habe die <a href="https://www.sfw-berlin.de/datenschutzerklaerung" target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>Datenschutzerklärung</a> gelesen und akzeptiere diese. *</span>
        </label>

        <button data-testid="submit-registration-btn" type="submit" disabled={submitting} style={{ width: "100%", padding: "12px", background: mode === "lateral" ? "#2563eb" : "#003366", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}>
          {submitting ? "Wird verarbeitet..." : mode === "lateral" ? "Unverbindliche Anfrage senden" : "Verbindlich anmelden"}
        </button>
      </form>
    </div>
  );
}
